import { Controller } from '@hotwired/stimulus'
import { Chart, Filler, LinearScale, LineController, LineElement, PointElement, Tooltip } from 'chart.js'

Chart.register(Filler, LinearScale, LineController, LineElement, PointElement, Tooltip)

const DESKTOP_QUERY = '(min-width: 992px)'
const GOLD = '#C49D38'
const GRID_COLOR = '#3D414F'
const GRID_LINES_RATIOS = [45 / 190, 95 / 190, 145 / 190]
// On the 190px maquette, the curve goes from 40px above the bottom to 24px below the top.
const CURVE_HEIGHT = 190 - 40 - 24
const BOTTOM_MARGIN_RATIO = 40 / CURVE_HEIGHT
const TOP_MARGIN_RATIO = 24 / CURVE_HEIGHT

// Draws the three dashed horizontal lines of the maquette, whatever the values.
const gridLinesPlugin = {
  id: 'grossInterestsGridLines',
  beforeDatasetsDraw(chart) {
    const { ctx, chartArea: { left, right, top, height } } = chart

    ctx.save()
    ctx.strokeStyle = GRID_COLOR
    ctx.lineWidth = 1
    ctx.setLineDash([2, 5])
    GRID_LINES_RATIOS.forEach((ratio) => {
      const y = Math.round(top + height * ratio) + 0.5
      ctx.beginPath()
      ctx.moveTo(left, y)
      ctx.lineTo(right, y)
      ctx.stroke()
    })
    ctx.restore()
  },
}

// Draws the vertical guide of the hovered point.
const hoverGuidePlugin = {
  id: 'grossInterestsHoverGuide',
  afterDatasetsDraw(chart) {
    const [active] = chart.tooltip?.getActiveElements() || []
    if (!active) return

    const { ctx, chartArea: { top, bottom } } = chart
    const x = Math.round(active.element.x) + 0.5

    ctx.save()
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(x, top)
    ctx.lineTo(x, bottom)
    ctx.stroke()
    ctx.restore()
  },
}

export default class extends Controller {
  static values = { data: Object }

  async connect() {
    this.canvas = this.element.querySelector('canvas')
    this.mediaQuery = window.matchMedia(DESKTOP_QUERY)
    this.onBreakpointChange = this.updateTicksPadding.bind(this)
    this.mediaQuery.addEventListener('change', this.onBreakpointChange)

    await document.fonts.ready
    if (!this.element.isConnected) return

    this.chart = new Chart(this.canvas, {
      type: 'line',
      data: { datasets: this.datasets() },
      options: this.options(),
      plugins: [gridLinesPlugin, hoverGuidePlugin],
    })
  }

  disconnect() {
    this.mediaQuery.removeEventListener('change', this.onBreakpointChange)
    this.chart?.destroy()
    this.tooltipElement?.remove()
  }

  datasets() {
    return this.dataValue.datasets.map((dataset) => ({
      ...dataset,
      borderColor: GOLD,
      borderWidth: 2.5,
      borderCapStyle: 'round',
      cubicInterpolationMode: 'monotone',
      fill: 'start',
      backgroundColor: this.fillGradient,
      pointRadius: ({ dataIndex }) => (dataIndex === dataset.data.length - 1 ? 5 : 0),
      pointBackgroundColor: '#F4EF8B',
      pointBorderColor: '#FFFFFF',
      pointBorderWidth: 2,
      pointHitRadius: 0,
      pointHoverRadius: 5,
      pointHoverBackgroundColor: '#987D40',
      pointHoverBorderColor: '#FFFFFF',
      pointHoverBorderWidth: 2,
    }))
  }

  fillGradient({ chart }) {
    const { ctx, chartArea } = chart
    if (!chartArea) return 'transparent'

    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
    gradient.addColorStop(0, 'rgba(196, 157, 56, 0.35)')
    gradient.addColorStop(1, 'rgba(196, 157, 56, 0)')
    return gradient
  }

  options() {
    const { ticks, min, max } = this.dataValue
    const labels = new Map(ticks.map(({ value, label }) => [value, label]))

    return {
      maintainAspectRatio: false,
      animation: false,
      layout: { padding: { right: 6 } },
      interaction: { mode: 'nearest', axis: 'x', intersect: false },
      scales: {
        x: {
          type: 'linear',
          min,
          max,
          grid: { display: false, drawBorder: false },
          afterBuildTicks: (axis) => { axis.ticks = ticks.map(({ value }) => ({ value })) },
          ticks: {
            align: 'inner',
            autoSkipPadding: 16,
            maxRotation: 0,
            padding: this.ticksPadding(),
            color: '#8B91A5',
            font: { family: 'DIN', size: 12, lineHeight: 1.25 },
            callback: (value) => labels.get(value),
          },
        },
        y: {
          display: false,
          ...this.yBounds(),
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false, external: this.renderTooltip.bind(this) },
      },
    }
  }

  yBounds() {
    const values = this.dataValue.datasets.flatMap(({ data }) => data.map(({ y }) => y))
    const lowest = Math.min(...values)
    const highest = Math.max(...values)
    const range = highest - lowest || Math.max(highest, 1)

    return { min: lowest - range * BOTTOM_MARGIN_RATIO, max: highest + range * TOP_MARGIN_RATIO }
  }

  ticksPadding() {
    return this.mediaQuery.matches ? 8 : 12
  }

  updateTicksPadding() {
    if (!this.chart) return

    this.chart.options.scales.x.ticks.padding = this.ticksPadding()
    this.chart.update()
  }

  renderTooltip({ chart, tooltip }) {
    const element = this.tooltip()

    if (tooltip.opacity === 0 || !tooltip.dataPoints?.length) {
      element.hidden = true
      return
    }

    const [{ raw, element: point }] = tooltip.dataPoints
    element.querySelector('[data-tooltip="date"]').textContent = raw.date
    element.querySelector('[data-tooltip="total"]').textContent = raw.total
    const detail = element.querySelector('[data-tooltip="detail"]')
    detail.textContent = raw.detail || ''
    detail.hidden = !raw.detail
    element.hidden = false

    // Placed 48px on the left of the point like the maquette, on its right when there is no room.
    const gap = 48
    const { width } = element.getBoundingClientRect()
    const left = point.x - gap - width >= 0 ? point.x - gap - width : Math.min(point.x + gap, chart.width - width)
    element.style.left = `${Math.max(left, 0)}px`
    element.style.top = `${chart.chartArea.top + 2}px`
  }

  tooltip() {
    if (this.tooltipElement) return this.tooltipElement

    this.tooltipElement = document.createElement('div')
    this.tooltipElement.className = 'gross-interests-chart__tooltip'
    this.tooltipElement.hidden = true
    this.tooltipElement.setAttribute('aria-hidden', 'true')
    this.tooltipElement.innerHTML = `
      <span class="gross-interests-chart__tooltip-date" data-tooltip="date"></span>
      <span class="gross-interests-chart__tooltip-total" data-tooltip="total"></span>
      <span class="gross-interests-chart__tooltip-detail" data-tooltip="detail"></span>
    `
    this.element.appendChild(this.tooltipElement)
    return this.tooltipElement
  }
}
